import * as React from 'react';
import { Dispatch } from 'react-redux';
import { Translate } from 'react-redux-i18n';
import { StoreState } from '../../../reducers';
import { Form, DocumentsBlock, SharePanelContainer, FormHeader, FormBody, FormBodyInner, SwitchLabel, ButtonCancel, ButtonSend, Loader } from './styles';
import { FormField } from './typings';
import Accordion from '../../Accordion';
import { FormState, SelectedNote } from '../../../containers/Share/reducer';
import { shareItems, setCrmContacts, SharePanelActionKeys, finishShare, closeShareActionsPopup, initiateShare } from '../../../containers/Share/actions';
import SharedDocuments from '../../../containers/Share/SharedDocuments';
import SharedNotes from '../../../containers/Share/SharedNotes';
import HeaderBlock from '../../HeaderBlock';
import UprezShareFormField from '../../../containers/Share/UprezForm/Field';
import ShareActionPopup from './ShareActionPopup';
import ShareViaCopyPopup from '../../../containers/Share/UprezForm/ShareViaCopyPopup';
import Switch from '../../Form/Switch';
import Utils from '../../../utils';
import ApiUtils from '../../../utils/api';
import { Api } from '../../../typings/api';
import { CallbackAction } from '../../../containers/DriveItemList/actions';

interface UprezShareFormProps {
	form: FormState;
	selectedResources: Api.DriveItem[];
	selectedNotes: SelectedNote[];
	isOpen: boolean;
	isShareActionsPopupOpen: boolean;
	isProcessing: boolean;
	isCreateDealAvailable: boolean;
	actionButtonColor: string;
	isReadOnlyApp: boolean;
	callbackAction: CallbackAction | undefined;
	dispatch: Dispatch<StoreState>;
}

const headerFields = [
	FormField.Email,
	FormField.Subject,
	FormField.Message
];

const contactFields = [
	FormField.Firstname,
	FormField.Surname,
	FormField.Company,
	FormField.Email,
	FormField.Phone,
	FormField.Position
];

const UprezShareForm = ({
	form,
	selectedResources,
	selectedNotes,
	isOpen,
	isShareActionsPopupOpen,
	isProcessing,
	isCreateDealAvailable,
	actionButtonColor,
	isReadOnlyApp,
	callbackAction,
	dispatch,
}: UprezShareFormProps) => {
	React.useEffect(() => {
		if (isOpen && !isReadOnlyApp) {
			ApiUtils.getCrmAccounts()
				.then((crmContacts) => dispatch(setCrmContacts(crmContacts)))
				.catch(() => dispatch(setCrmContacts([])));
		}
	}, [isOpen, isReadOnlyApp]);

	const handleSendForm = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		dispatch(shareItems());
	};

	const renderFields = (fields: FormField[], isHeader: boolean) =>
		fields.map((field, index) => (
			<UprezShareFormField
				key={`${name}${index}`}
				fieldName={field}
				isHeader={isHeader}
			/>
		))
	;

	const handleCreateDealChange = () => {
		const { createDeal } = form;
		dispatch({
			type: SharePanelActionKeys.SET_FORM_FIELD,
			name: FormField.CreateDeal,
			value: !createDeal,
		});
	};

	const handleShareViaCopy = () => {
		dispatch(initiateShare(selectedResources, false, Api.ShareNetwork.Copy, callbackAction));
	};

	const handleShareViaUprez = () => {
		dispatch(closeShareActionsPopup());
		dispatch(initiateShare(selectedResources, false, Api.ShareNetwork.Uprez, callbackAction));
	};

	const { contactEmail, createDeal } = form;
	const hasMultipleContacts = contactEmail.includes(',');

	return (
		<>
			<ShareActionPopup
				isDisplayed={isShareActionsPopupOpen}
				isProcessing={isProcessing}
				onShareViaCopy={handleShareViaCopy}
				onShareViaUprez={handleShareViaUprez}
				onCancel={() => dispatch(closeShareActionsPopup(true))}
			/>
			<ShareViaCopyPopup />
			<SharePanelContainer isOpen={isOpen} data-gallery-slot="shareForm">
				{isProcessing &&
					<Loader>
						{Utils.getSvgIcon('iconset_loading')}
						<Translate value="messages.loading" />
					</Loader>
				}
				<Form onSubmit={handleSendForm}>
					<FormHeader>
						<ButtonCancel
							type="button"
							onClick={() => dispatch(finishShare())}
							color={actionButtonColor}
						>
							<Translate value="messages.cancel" />
						</ButtonCancel>
						{isCreateDealAvailable && !isReadOnlyApp &&
							<Switch
								name="createDeal"
								isActive={createDeal}
								style={{
									activeColor: actionButtonColor,
								}}
								onChange={handleCreateDealChange}
								label={(
									<SwitchLabel
										value="messages.create_deal_in_crm"
										dangerousHTML={true}
									/>
								)}
							/>
						}
						<ButtonSend
							type="submit"
							disabled={isProcessing}
							color={actionButtonColor}
						>
							<Translate value="messages.send" />
						</ButtonSend>
					</FormHeader>
					<FormBody>
						<FormBodyInner>
							{renderFields(headerFields, true)}
							{!hasMultipleContacts &&
								<Accordion
									title={`share.${FormField.Contact}`}
									isOpened={false}
								>
									{renderFields(contactFields, false)}
								</Accordion>
							}
							{selectedResources.length > 0 &&
								<DocumentsBlock>
									<HeaderBlock title={`share.${FormField.Documents}`} />
									<SharedDocuments />
								</DocumentsBlock>
							}
							{selectedNotes.length > 0 &&
								<DocumentsBlock>
									<HeaderBlock title={`share.${FormField.Notes}`} />
									<SharedNotes />
								</DocumentsBlock>
							}
						</FormBodyInner>
					</FormBody>
				</Form>
			</SharePanelContainer>
		</>
	);
};

export default UprezShareForm;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Share/UprezForm/index.tsx